import { useState, useEffect, useRef } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer, PenTool, FileCheck, FileSignature
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { API, getAuthHeaders, formatMontant, openAuthedPdf } from "./helpers";
import { toast } from "sonner";

export function PreparerSignerTab({ dossierId, dossier, tenantId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [docusignConfigured, setDocusignConfigured] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [docToSign, setDocToSign] = useState(null);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signatureForm, setSignatureForm] = useState({
    signerEmail: "", signerName: "", emailSubject: "", emailMessage: ""
  });
  const [signatureStatuses, setSignatureStatuses] = useState({});
  const [generatingMandat, setGeneratingMandat] = useState(false);
  const [generatingAvance, setGeneratingAvance] = useState(false);
  const [generatingProcuration, setGeneratingProcuration] = useState(false);

  useEffect(() => {
    loadSignableDocs();
    checkDocuSignConfig();
  }, [dossierId]);

  const checkDocuSignConfig = async () => {
    try {
      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/docusign/config`, { headers: hdrs, credentials: 'include' });
      if (res.ok) {
        const config = await res.json();
        setDocusignConfigured(config.status === "configured" || config.details?.configured === true);
      }
    } catch (e) {
      console.warn("DocuSign config check failed:", e);
    }
  };

  const loadSignableDocs = async () => {
    setLoading(true);
    try {
      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`, {
        headers: hdrs, credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDocs(data.documents || []);
      }
    } catch (e) {
      console.error("Erreur chargement docs signables:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMandat = async () => {
    setGeneratingMandat(true);
    try {
      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/mandat-amo/generer`,
        { method: "POST", headers: hdrs, credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success("Mandat ANAH généré", {
          description: `Mandataire : ${data?.mandataire?.raison_sociale || "—"}`,
        });
        await loadSignableDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Erreur génération du Mandat ANAH");
      }
    } catch (e) {
      console.error("Erreur génération mandat:", e);
      toast.error("Erreur génération du Mandat ANAH");
    } finally {
      setGeneratingMandat(false);
    }
  };

  const handleGenerateAvance = async () => {
    setGeneratingAvance(true);
    try {
      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/demande-avance-anah/generer`,
        { method: "POST", headers: hdrs, credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success("Demande d'avance ANAH générée", {
          description: data?.reference_anah
            ? `N° dossier ANAH : ${data.reference_anah}`
            : "Document généré (n° de dossier ANAH non renseigné)",
        });
        await loadSignableDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail?.message || err?.detail || "Erreur génération de la Demande d'avance");
      }
    } catch (e) {
      console.error("Erreur génération demande d'avance:", e);
      toast.error("Erreur génération de la Demande d'avance");
    } finally {
      setGeneratingAvance(false);
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.pdf_url) return;
    setDownloadingId(doc.id);
    try {      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(`${API}${doc.pdf_url}`, { headers: hdrs, credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.pdf_filename || `${doc.type_document}_${dossierId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      toast.error("Erreur téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  // Cerfa 13463-02 (Procuration) : GÉNÉRÉ pré-rempli avec les données du
  // dossier (mandant = bénéficiaire, mandataire = AMO), EXACTEMENT comme le
  // Mandat ANAH (P-069). Persisté en metier_devis -> download rempli + envoi
  // signature élec (DocuSign). Plus de téléchargement de modèle vierge.
  const handleGenerateProcuration = async () => {
    setGeneratingProcuration(true);
    try {
      const hdrs = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/procuration-seing-prive/generer`,
        { method: "POST", headers: hdrs, credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success("Procuration (Cerfa 13463) générée", {
          description: data?.mandataire?.raison_sociale
            ? `Mandataire : ${data.mandataire.raison_sociale}`
            : "Document rempli avec les données du dossier",
        });
        await loadSignableDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail?.message || err?.detail || "Erreur génération de la Procuration");
      }
    } catch (e) {
      console.error("Erreur génération procuration:", e);
      toast.error("Erreur génération de la Procuration");
    } finally {
      setGeneratingProcuration(false);
    }
  };

  // Voir = ouvrir le PDF dans un nouvel onglet (mockup : Voir + Télécharger + Signer).
  // Fix pop-up blocker : openAuthedPdf ouvre l'onglet DANS le geste utilisateur
  // (avant l'await), sinon le navigateur bloque le window.open post-fetch.
  const handleView = async (doc) => {
    if (!doc.pdf_url) return;
    await openAuthedPdf({
      url: `${API}${doc.pdf_url}`,
      headers: getAuthHeaders(tenantId),
      onError: (msg) => toast.error(msg || "Erreur à l'ouverture"),
    });
  };

  const handleOpenSignatureDialog = (doc) => {
    if (!docusignConfigured) {
      toast.error("DocuSign n'est pas configuré", {
        description: "Configurez DocuSign dans Gouvernance > Intégrations"
      });
      return;
    }
    // Pré-remplir avec les infos client
    const clientNom = dossier?.client_nom || "";
    const clientEmail = dossier?.client_email || dossier?.client?.email || "";
    setSignatureForm({
      signerName: clientNom,
      signerEmail: clientEmail,
      emailSubject: `Document ${doc.type_document} à signer - ${dossier?.référence || ""}`,
      emailMessage: `Bonjour,\n\nVeuillez trouver ci-joint le document "${doc.type_document}" à signer pour votre dossier MaPrimeAdapt'.\n\nCordialement`
    });
    setDocToSign(doc);
    setShowSignatureDialog(true);
  };

  const handleSendForSignature = async () => {
    if (!docToSign) return;
    if (!signatureForm.signerEmail || !signatureForm.signerName) {
      toast.error("Remplissez l'email et le nom du signataire");
      return;
    }
    setSendingSignature(true);
    try {
      const hdrs = getAuthHeaders(tenantId);
      // Récupérer le PDF
      const pdfRes = await fetch(`${API}${docToSign.pdf_url}`, { headers: hdrs, credentials: 'include' });
      if (!pdfRes.ok) throw new Error("Impossible de récupérer le PDF");
      const pdfBlob = await pdfRes.blob();

      // Envoyer à DocuSign
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", pdfBlob, `${docToSign.type_document}.pdf`);
      formData.append("signer_email", signatureForm.signerEmail);
      formData.append("signer_name", signatureForm.signerName);
      formData.append("email_subject", signatureForm.emailSubject || "Document à signer");
      formData.append("email_message", signatureForm.emailMessage || "");
      if (dossier?.id) formData.append("dossier_id", dossier.id);

      const sendRes = await fetch(`${API}/api/docusign/send-pdf`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId || "tef",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: formData
      });
      if (!sendRes.ok) {
        const errData = await sendRes.json().catch(() => ({}));
        throw new Error(errData.detail || "Erreur lors de l'envoi");
      }
      toast.success("Document envoyé pour signature !", {
        description: `Email envoyé à ${signatureForm.signerEmail}`
      });
      setShowSignatureDialog(false);
    } catch (err) {
      console.error("Erreur envoi DocuSign:", err);
      toast.error(err.message || "Erreur lors de l'envoi DocuSign");
    } finally {
      setSendingSignature(false);
    }
  };

  // Déterminer si un mandataire est défini → afficher le mandat
  // Source de vérité : backend écrit à dossier.donnees_mpa (AMO, V1.5) ; fallback
  // donnees_mpr (transition) + legacy workflow_data.
  const donneesMpr = dossier?.donnees_mpa
    || dossier?.donnees_mpr
    || dossier?.workflow_data?.donnees_mpr
    || dossier?.workflow_data?.tef?.donnees_mpr
    || {};
  const hasMandataire = !!(donneesMpr.mandataire_id);

  const SIGN_ITEMS = [
    // Mandat visible uniquement si un mandataire est sélectionné dans Données MPA
    ...(hasMandataire ? [{ key: "mandat_amo", label: "Mandat AMO", icon: FileText, description: "Mandat d'assistance à maîtrise d'ouvrage" }] : []),
    { key: "devis_amo", label: "Devis AMO", icon: Receipt, description: "Devis d'accompagnement AMO MaPrimeAdapt'" },
    { key: "contrat_amo", label: "Contrat AMO", icon: FileCheck, description: "Contrat d'accompagnement AMO" },
    { key: "demande_avance_anah", label: "Demande d'avance ANAH", icon: FileSignature, description: "Cerfa 13934*04 — avance jusqu'à 70% de la subvention" },
    // CERFA 13463-02 (après travaux) : GÉNÉRÉ pré-rempli comme le mandat/avance
    // (P-069), plus de modèle vierge. Matché sur type_document en base.
    { key: "procuration_seing_prive", label: "Procuration sous seing privé", icon: FileSignature, description: "Cerfa 13463-02 — procuration de perception des fonds (après travaux)" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }

  return (
    <div className="rounded-xl p-6" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="preparer-signer-tab">
      <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
        <PenTool className="w-5 h-5 text-[var(--oh-accent-text)]" />
        Préparer & Signer
      </h3>
      <p className="text-sm mb-6" style={{ color: "var(--oh-text-muted)" }}>
        Documents à préparer pour signature client. Téléchargez, faites signer, puis ré-importez le document signé.
      </p>

      <div className="space-y-3">
        {SIGN_ITEMS.map((item) => {
          const doc = docs.find(d => d.type_document === item.key);
          const isGenerated = doc && (doc.pdf_genere || doc.pdf_url);
          const isSigned = doc?.is_signed;
          const IconComp = item.icon;

          return (
            <div
              key={item.key}
              className="flex items-center justify-between p-4 rounded-lg transition-colors"
              style={{
                background: isSigned ? "rgba(16,185,129,0.06)" : isGenerated ? "var(--oh-bg-surface)" : "rgba(245,158,11,0.04)",
                border: `1px solid ${isSigned ? "rgba(16,185,129,0.2)" : isGenerated ? "var(--oh-border)" : "rgba(245,158,11,0.15)"}`
              }}
              data-testid={`sign-item-${item.key}`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: isGenerated ? "rgba(59,130,246,0.1)" : "rgba(245,158,11,0.1)" }}>
                  <IconComp className={`w-5 h-5 ${isGenerated ? "text-[var(--oh-accent-text)]" : "text-[var(--oh-info-text)]"}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm" style={{ color: "var(--oh-text)" }}>{item.label}</span>
                    {isSigned && (
                      <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0 text-[10px]">Signé</Badge>
                    )}
                    {isGenerated && !isSigned && (
                      <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border-0 text-[10px]">Généré</Badge>
                    )}
                    {!isGenerated && (
                      <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-0 text-[10px]">Non généré</Badge>
                    )}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>{item.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!isGenerated && item.key === "procuration_seing_prive" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateProcuration}
                    disabled={generatingProcuration}
                    className="text-[var(--oh-stat-cyan-text)] border-[var(--oh-stat-cyan-border)] hover:bg-[var(--oh-stat-cyan-bg)]"
                    data-testid="generate-procuration-btn"
                  >
                    {generatingProcuration ? (
                      <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <FileSignature className="w-3 h-3 mr-1" />
                    )}
                    Générer
                  </Button>
                )}
                {!isGenerated && item.key === "mandat_amo" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateMandat}
                    disabled={generatingMandat}
                    className="text-[var(--oh-stat-cyan-text)] border-[var(--oh-stat-cyan-border)] hover:bg-[var(--oh-stat-cyan-bg)]"
                    data-testid="generate-mandat-amo-btn"
                  >
                    {generatingMandat ? (
                      <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <FileSignature className="w-3 h-3 mr-1" />
                    )}
                    Générer
                  </Button>
                )}
                {!isGenerated && item.key === "demande_avance_anah" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateAvance}
                    disabled={generatingAvance}
                    className="text-[var(--oh-stat-cyan-text)] border-[var(--oh-stat-cyan-border)] hover:bg-[var(--oh-stat-cyan-bg)]"
                    data-testid="generate-demande-avance-anah-btn"
                  >
                    {generatingAvance ? (
                      <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <FileSignature className="w-3 h-3 mr-1" />
                    )}
                    Générer
                  </Button>
                )}
                {isGenerated && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(doc)}
                      data-testid={`view-sign-${item.key}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Voir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                      data-testid={`download-sign-${item.key}`}
                    >
                      {downloadingId === doc.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Download className="w-3 h-3 mr-1" />
                      )}
                      Télécharger
                    </Button>
                    {docusignConfigured && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenSignatureDialog(doc)}
                        className="text-[var(--oh-accent-text)] border-[var(--oh-accent)] hover:bg-[var(--oh-accent-bg)]"
                        data-testid={`docusign-send-${item.key}`}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        DocuSign
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* DocuSign info banner */}
      {docusignConfigured && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]">
          <FileSignature className="w-4 h-4 text-[var(--oh-accent-text)]" />
          <span className="text-sm text-[var(--oh-accent-text)]">
            DocuSign configuré — Envoyez vos documents pour signature électronique
          </span>
        </div>
      )}

      {/* Dialog de signature DocuSign */}
      {showSignatureDialog && docToSign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--oh-bg)] backdrop-blur-sm">
          <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-[var(--oh-accent-bg)]">
                <FileSignature className="w-5 h-5 text-[var(--oh-accent-text)]" />
              </div>
              <div>
                <h3 className="text-[var(--oh-text)] font-medium">Envoyer pour signature</h3>
                <p className="text-sm text-[var(--oh-text-secondary)]">{docToSign.type_document}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm text-[var(--oh-text-secondary)] block mb-1">Nom du signataire *</label>
                <Input
                  value={signatureForm.signerName}
                  onChange={(e) => setSignatureForm(prev => ({ ...prev, signerName: e.target.value }))}
                  placeholder="Prénom Nom"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  data-testid="docusign-signer-name"
                />
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-secondary)] block mb-1">Email du signataire *</label>
                <Input
                  type="email"
                  value={signatureForm.signerEmail}
                  onChange={(e) => setSignatureForm(prev => ({ ...prev, signerEmail: e.target.value }))}
                  placeholder="email@exemple.fr"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  data-testid="docusign-signer-email"
                />
              </div>
              <div>
                <label className="text-sm text-[var(--oh-text-secondary)] block mb-1">Objet de l'email</label>
                <Input
                  value={signatureForm.emailSubject}
                  onChange={(e) => setSignatureForm(prev => ({ ...prev, emailSubject: e.target.value }))}
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  data-testid="docusign-email-subject"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowSignatureDialog(false)}
                disabled={sendingSignature}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
                onClick={handleSendForSignature}
                disabled={sendingSignature || !signatureForm.signerEmail || !signatureForm.signerName}
                data-testid="docusign-send-button"
              >
                {sendingSignature ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer pour signature
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
